import * as React from "react";
import { last } from "lodash-es";
import { useAppWebSocketCtx, useWebSocketListeners, } from "@warda/library-ui/contexts/AppWebSocketContext";
import { Initialize } from "@warda/library-ui/interfaces";
import { isMobile } from "@warda/library-ui/utils/deviceUtils";
import inLoading from "@warda/library-ui/utils/inLoading";
import Placeholder from "@warda/library-ui/core/Placeholder";
import emptyFn from "@warda/library-ui/utils/emptyFn";
import reducer, { reducerInitState, ACT_VPORT } from "./reducer";
import useSearchEs, { searchFiles } from "./useSearchEs/useSearchEs";
import View from "./View";
import ToolbarActions from "./ToolbarActions";
import DialogDetail from "./DialogDetail";
import DialogViewer from "./DialogViewer";
import DialogConfirmDownload from "./DialogConfirmDownload";
import { AccessStatus, getTenantId } from "./constants";
import setThemeDefault from "../../utils/setThemeDefault";
import webSocketCallbacks from "./websocketCallbacks";
import BtnRefresh from "./Buttons/BtnRefresh";
import BtnDetail from "./Buttons/BtnDetail";
import BtnDownloads from "./Buttons/BtnDownloads";
import FiltersDesktop from "./Filters/FiltersDesktop";
import getSearchAfter from "../AreaMediaLibrary/AreaFiles/utils/getSearchAfter";
import SharePageFrame from "./SharePageFrame";
const CtxDispatchViewport = React.createContext(emptyFn);
export const useContextDispatchViewport = () => React.useContext(CtxDispatchViewport);
const getFolderParentId = (folderPath) => {
    try {
        const elasticId = last(folderPath).id;
        const folderId = parseInt(elasticId.split("_")[1]);
        return folderId;
    }
    catch {
        return undefined;
    }
};
const SharePage = ({ accessStatus, handleError, handleSerchResult, }) => {
    const [state, dispatch] = React.useReducer(reducer, reducerInitState);
    const { pageHrchId, pagePrntId, initSearch, initMsgErr, folderPath, files, filesSlcId, filesTotal, filters, filtersCondition, sortKey, sortOrd, viewType, viewMode, downloads, dialogDownload, dialogDetail, dialogViewer, initMore, moreType, logoReady, } = state;
    const tenantId = getTenantId();
    const prnt = getFolderParentId(folderPath);
    const { searchTime, setSearchTime, searchRes } = useSearchEs({
        hrch: pageHrchId,
        prnt,
        sortKey,
        sortOrd,
        filters,
        filtersCondition,
    });
    const searchLoading = inLoading(initSearch);
    const moreLoading = inLoading(initMore);
    useWebSocketListeners(webSocketCallbacks, dispatch);
    React.useEffect(() => {
        setThemeDefault();
    }, []);
    React.useEffect(() => {
        if (initSearch === Initialize.START) {
            dispatch({ type: ACT_VPORT.INIT_SEARCH_LOADING });
            setSearchTime(new Date().getTime());
        }
    }, [initSearch, setSearchTime]);
    // pass search res to reducer
    React.useEffect(() => {
        var _a;
        if (!!searchRes.result) {
            handleSerchResult((_a = searchRes.result) === null || _a === void 0 ? void 0 : _a.status);
            dispatch({
                type: ACT_VPORT.INIT_SEARCH_RESULT,
                result: searchRes.result,
            });
        }
    }, [handleSerchResult, searchRes.result]);
    // Avvio websocket
    const appWebSocketCtx = useAppWebSocketCtx();
    React.useEffect(() => {
        appWebSocketCtx.openWebSocket({
            anonymous: true,
            tenantId,
        });
    }, [appWebSocketCtx, tenantId]);
    React.useEffect(() => {
        (async () => {
            if (initMore === Initialize.START) {
                try {
                    dispatch({ type: ACT_VPORT.INIT_MORE_LOADING });
                    const searchAfter = getSearchAfter({ files, moreType });
                    const { files: moreFiles, filesTotal } = await searchFiles({
                        hrch: pageHrchId,
                        prnt,
                        sortKey,
                        sortOrd,
                        filters,
                        filtersCondition,
                        type: moreType,
                        searchAfter,
                    });
                    // NOTE [DEBITO TECNICO] Possono esserci elementi ripetuti.
                    // Questa casistica si presenta solo se l'utente del portale sta
                    // navigando mentre un utente SC sta caricando/spostando elementi del portale
                    // in quando qui basiamo il FROM sul conteggio elementi attuali.
                    // Andrebbe verifica  che non si stanno aggiungendo elementi già presenti.
                    // Se così fosse vuol dire che sono stati aggiunti elementi prima da recuperare
                    // e quelli già caricati non vanno duplicati.
                    dispatch({
                        type: ACT_VPORT.INIT_MORE_RESULT,
                        files: moreFiles,
                        filesTotal,
                    });
                }
                catch (error) {
                    console.error("Error loading additional data:", error);
                    dispatch({
                        type: ACT_VPORT.INIT_MORE_RESULT,
                        files: [],
                        filesTotal: files.length,
                    });
                }
            }
        })();
    }, [
        files,
        filters,
        filtersCondition,
        initMore,
        moreType,
        pageHrchId,
        prnt,
        sortKey,
        sortOrd,
    ]);
    const detail = files === null || files === void 0 ? void 0 : files.find((f) => f.id === last(filesSlcId));
    const handleLogoReady = React.useCallback(() => {
        dispatch({ type: ACT_VPORT.SET_LOGO_READY });
    }, []);
    return !logoReady ||
        (searchLoading && accessStatus === AccessStatus.VERIFYING) ? (React.createElement(SharePageFrame, { content: React.createElement(Placeholder, { spinner: true }), onLogoReady: handleLogoReady })) : (React.createElement(CtxDispatchViewport.Provider, { value: dispatch },
        React.createElement(SharePageFrame, { innerToolbar: React.createElement(React.Fragment, null,
                React.createElement(FiltersDesktop, { key: `sharePagefilters`, hidden: isMobile(), filters: filters }),
                React.createElement("div", { style: { flex: 1 } }),
                React.createElement(BtnDownloads, { downloads: downloads }),
                React.createElement(BtnRefresh, null),
                dialogDetail || isMobile() ? null : React.createElement(BtnDetail, null)), content: initSearch === Initialize.FAIL ? (accessStatus != AccessStatus.GRANTED ? (React.createElement(Placeholder, { spinner: true })) : (React.createElement(Placeholder, { icon: "live_help", label: "You can't access this page" }))) : (React.createElement(React.Fragment, null,
                React.createElement(ToolbarActions, { pagePrntId: pagePrntId, files: files, filesSlcId: filesSlcId, folderPath: folderPath, sortKey: sortKey, sortOrd: sortOrd, viewMode: viewMode, viewType: viewType, filters: filters }),
                React.createElement(View, { searchTime: searchTime, error: initMsgErr, viewMode: viewMode, viewType: viewType, files: files, filesSlcId: filesSlcId, filesTotal: filesTotal, loading: searchLoading, sortKey: sortKey, sortOrd: sortOrd, moreLoading: moreLoading, moreFailed: initMore === Initialize.FAIL }))) }),
        React.createElement(DialogConfirmDownload, { open: dialogDownload, files: files, filesSlcId: filesSlcId, pageHrchId: pageHrchId, handleError: handleError }),
        React.createElement(DialogDetail, { open: dialogDetail, file: detail }),
        React.createElement(DialogViewer, { open: dialogViewer, file: detail, handleError: handleError })));
};
export default SharePage;
